"use client";

/**
 * Presentational AI reply-assist toolbar. NO data fetching here — the consumer
 * passes `onRun(intent, instruction?)` and wires it to its own endpoint (via
 * `useAiReplyAssist`). Kept free of any app's UI kit so it drops into Hub,
 * next-email-client, or anywhere else with Tailwind + lucide-react.
 *
 * Behavior:
 *  - No draft yet  → a single "Help me write" affordance that opens a one-line
 *    description input → runs the `draft` intent.
 *  - Draft present → four one-click transforms (Polish / Formal / Friendly /
 *    Shorten) + a "Describe a change" freeform input that runs the `custom` intent.
 */

import { useState, type KeyboardEvent } from "react";
import {
  Briefcase,
  Loader2,
  Scissors,
  Smile,
  Sparkles,
  Wand2,
  X,
  type LucideIcon,
} from "lucide-react";
import { AI_REPLY_INTENTS, AI_REPLY_TRANSFORM_INTENTS } from "./intents.js";
import type { AiReplyIntent } from "./types.js";

const ICONS: Record<string, LucideIcon> = {
  Sparkles,
  Wand2,
  Briefcase,
  Smile,
  Scissors,
};

export interface AiReplyAssistToolbarProps {
  /** Whether the editor currently has draft text. Switches the mode. */
  hasBody: boolean;
  /** An AI request is in flight — disables controls + shows a spinner. */
  isLoading: boolean;
  /** Run an intent. `instruction` is supplied for `draft` + `custom`. */
  onRun: (intent: AiReplyIntent, instruction?: string) => void;
  /** Disable the whole toolbar (e.g. no email account selected). */
  disabled?: boolean;
  /** Last error message to surface inline, if any. */
  error?: string | null;
  className?: string;
}

const BTN =
  "inline-flex items-center gap-1.5 rounded-md border border-input bg-background px-2.5 py-1 text-xs font-medium text-foreground transition-colors hover:bg-accent hover:text-accent-foreground disabled:cursor-not-allowed disabled:opacity-50";

export function AiReplyAssistToolbar({
  hasBody,
  isLoading,
  onRun,
  disabled,
  error,
  className,
}: AiReplyAssistToolbarProps) {
  // `null` = closed; "draft"/"custom" = which instruction input is open.
  const [openInput, setOpenInput] = useState<null | "draft" | "custom">(null);
  const [instruction, setInstruction] = useState("");
  const blocked = Boolean(disabled) || isLoading;

  function submitInstruction(intent: "draft" | "custom") {
    const trimmed = instruction.trim();
    if (intent === "custom" && !trimmed) return;
    onRun(intent, trimmed || undefined);
    setInstruction("");
    setOpenInput(null);
  }

  function onInputKeyDown(e: KeyboardEvent<HTMLInputElement>, intent: "draft" | "custom") {
    if (e.key === "Enter") {
      // Prevent the surrounding compose <form> from submitting.
      e.preventDefault();
      submitInstruction(intent);
    } else if (e.key === "Escape") {
      e.preventDefault();
      setOpenInput(null);
      setInstruction("");
    }
  }

  return (
    <div className={`flex flex-col gap-1.5 ${className ?? ""}`}>
      <div className="flex flex-wrap items-center gap-1.5">
        <span className="inline-flex items-center gap-1 text-xs font-medium text-muted-foreground">
          <Sparkles className="h-3.5 w-3.5" aria-hidden />
          AI
        </span>

        {!hasBody ? (
          <button
            type="button"
            className={BTN}
            disabled={blocked}
            onClick={() => setOpenInput((v) => (v === "draft" ? null : "draft"))}
            aria-expanded={openInput === "draft"}
          >
            {isLoading ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />
            ) : (
              <Sparkles className="h-3.5 w-3.5" aria-hidden />
            )}
            {AI_REPLY_INTENTS.draft.label}
          </button>
        ) : (
          <>
            {AI_REPLY_TRANSFORM_INTENTS.map((intent) => {
              const meta = AI_REPLY_INTENTS[intent];
              const Icon = ICONS[meta.icon] ?? Wand2;
              return (
                <button
                  key={intent}
                  type="button"
                  className={BTN}
                  disabled={blocked}
                  title={meta.description}
                  onClick={() => onRun(intent)}
                >
                  {isLoading ? (
                    <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />
                  ) : (
                    <Icon className="h-3.5 w-3.5" aria-hidden />
                  )}
                  {meta.label}
                </button>
              );
            })}
            <button
              type="button"
              className={BTN}
              disabled={blocked}
              onClick={() => setOpenInput((v) => (v === "custom" ? null : "custom"))}
              aria-expanded={openInput === "custom"}
              title={AI_REPLY_INTENTS.custom.description}
            >
              <Wand2 className="h-3.5 w-3.5" aria-hidden />
              {AI_REPLY_INTENTS.custom.label}
            </button>
          </>
        )}
      </div>

      {openInput && (
        <div className="flex items-center gap-1.5">
          <input
            type="text"
            autoFocus
            value={instruction}
            disabled={blocked}
            onChange={(e) => setInstruction(e.target.value)}
            onKeyDown={(e) => onInputKeyDown(e, openInput)}
            placeholder={
              openInput === "draft"
                ? "Describe the email you want…"
                : "Describe the change you want…"
            }
            className="h-8 flex-1 rounded-md border border-input bg-background px-2.5 text-xs outline-none focus-visible:ring-1 focus-visible:ring-ring"
          />
          <button
            type="button"
            className={BTN}
            disabled={blocked || (openInput === "custom" && !instruction.trim())}
            onClick={() => submitInstruction(openInput)}
          >
            {isLoading ? <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden /> : null}
            {openInput === "draft" ? "Write" : "Apply"}
          </button>
          <button
            type="button"
            className={BTN}
            aria-label="Cancel"
            onClick={() => {
              setOpenInput(null);
              setInstruction("");
            }}
          >
            <X className="h-3.5 w-3.5" aria-hidden />
          </button>
        </div>
      )}

      {error ? <p className="text-xs text-destructive">{error}</p> : null}
    </div>
  );
}
